import { apiFetch, ApiError } from './client';
import { commissionKey } from '../lib/keys';
import type {
  Catalog,
  Character,
  Commission,
  FamilyMe,
  GateVerdict,
  InFlightCommission,
  PlaybackResponse,
  Starter,
  Video,
} from './types';

/* ------------------------------------------------------------------ family */

export function getFamilyMe(): Promise<FamilyMe> {
  return apiFetch<FamilyMe>('/families/me', { note: 'balance + ledger' });
}

export function createFamily(name = ''): Promise<{ familyId: string; creditBalance: number }> {
  return apiFetch('/families', { method: 'POST', body: { name }, note: 'bootstrap family' });
}

/**
 * An Entra user and a family row are created at different moments: the
 * directory user exists as soon as an admin makes it, the row only once the
 * app runs. So the FIRST load of every new account 404s here. Create on demand
 * and retry.
 *
 * Safe to call repeatedly — families.py keys the partition on the principal
 * (the token's `oid`, not `sub`, which Entra makes pairwise per application)
 * and swallows the conditional-check failure, so it never double-grants
 * credits.
 */
export async function ensureFamily(): Promise<FamilyMe> {
  try {
    return await getFamilyMe();
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err;
    await createFamily();
    return getFamilyMe();
  }
}

/* -------------------------------------------------------------------- gate */

export function postGate(text: string, speculative = false): Promise<GateVerdict> {
  return apiFetch<GateVerdict>('/gate', {
    method: 'POST',
    body: { text },
    speculative,
    note: speculative ? 'speculative gate' : 'gate',
  });
}

/* ----------------------------------------------------------------- catalog */

/**
 * Open route (no auth). Accepts both the deployed shape — a bare array of
 * {slug,label,desc} with no URLs and no version — and the reshaped
 * {catalogVersion, starters:[...]} the spec calls for. Normalizing here means
 * no screen has to know which backend it is talking to.
 */
export async function getStarters(): Promise<Catalog> {
  const raw = await apiFetch<unknown>('/catalog/starters', {
    anonymous: true,
    note: 'starter catalog',
  });

  if (Array.isArray(raw)) {
    return { catalogVersion: 'unversioned', starters: raw as Starter[] };
  }
  const obj = (raw ?? {}) as Record<string, unknown>;
  const starters = Array.isArray(obj.starters) ? (obj.starters as Starter[]) : [];
  return {
    catalogVersion: typeof obj.catalogVersion === 'string' ? obj.catalogVersion : 'unversioned',
    styleId: typeof obj.styleId === 'string' ? obj.styleId : undefined,
    starters,
  };
}

/* -------------------------------------------------------------- characters */

/**
 * THE FAMILY'S OWN CAST.
 *
 * WHY IT EXISTS: the gallery listed the platform catalog and nothing else, so
 * a character a family had made was reachable exactly once — in the wizard run
 * that made it — and then vanished from the product. Jamie, on the live site:
 * "I can't find the green helicopter etc., because it's gone from the list."
 * The route was already there and already deployed; nothing in the browser had
 * ever called it.
 *
 * The route lists BY FAMILY off a secondary index (api/characters.py:_list —
 * a query, not a scan) and returns the same character view GET /characters/{id}
 * does, newest first. No backend change was needed: verified against the
 * deployed Function App on 2026-08-24 with a real family token, which answered
 * 200 with `chosen`, `heroUrl`, `name` and `commissionable` all populated.
 *
 * Both wire shapes for the same reason getStarters() takes two — an envelope
 * and a bare array are equally likely from a handler that gets reshaped, and
 * neither is worth an empty gallery. The deployed one is the envelope.
 *
 * 404 IS AN EMPTY LIST, NOT A FAULT — and this one was caught in a browser
 * against the live API, on the parent's very first screen.
 *
 * shared/responses.py answers 404 {"error":"no family yet"} for a principal
 * whose family row does not exist. On a first run it does not exist YET:
 * App.tsx creates it from ensureFamily(), and that effect and the gallery's
 * mount are in the same React commit, so GET /characters and POST /families
 * race. The gallery lost that race and painted a red error box reading
 * "your characters: no family yet" over the catalog — the FIRST thing a
 * brand-new parent ever sees, blaming them for an account we had not finished
 * making. Reproduced at localhost against the deployed Function App on
 * 2026-08-24.
 *
 * A missing family has no characters; a route that is not deployed has no
 * characters either. Both are truthfully "nothing of yours to show", which is
 * a section that does not render — see ownCharacters() in screens/Gallery.tsx.
 * Every other status still throws: a 401 is a dead session and a 500 is a
 * broken API, and the gallery says so. Same bargain, and for the same reason,
 * as getInFlightCommissions() below.
 */
export async function getCharacters(): Promise<Character[]> {
  let raw: unknown;
  try {
    raw = await apiFetch<unknown>('/characters', { note: "the family's characters" });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
  if (Array.isArray(raw)) return raw as Character[];
  const rows = (raw as { characters?: unknown } | null)?.characters;
  return Array.isArray(rows) ? (rows as Character[]) : [];
}

export type CreateCharacterInput = {
  starterSlug?: string;
  /** Fallback text for the starter path — see the comment in createCharacter. */
  starterDesc?: string;
  text?: string;
  gateResult?: GateVerdict;
  idempotencyKey: string;
};

export type CreateCharacterResult = {
  characterId: string;
  status?: string;
  candidates?: Character['candidates'];
};

export async function createCharacter(input: CreateCharacterInput): Promise<CreateCharacterResult> {
  if (input.starterSlug) {
    try {
      // A catalog tile is already vetted, so the reshaped backend takes the
      // slug alone and skips the gate entirely (no Bedrock call, no latency).
      return await post({ starterSlug: input.starterSlug }, input.idempotencyKey);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 400) throw err;
      // The DEPLOYED characters.py has no starterSlug path and 400s on
      // "text required". Retrying with the starter's own description keeps
      // the click-through alive against today's backend; the idempotency key
      // is unchanged, so a server that DID create something cannot fork.
      return await post(
        { starterSlug: input.starterSlug, text: input.starterDesc ?? input.starterSlug },
        input.idempotencyKey,
      );
    }
  }

  const text = (input.text ?? '').trim();
  if (!text) throw new Error('createCharacter needs a starterSlug or text');
  return post({ text, gateResult: input.gateResult }, input.idempotencyKey);
}

function post(body: Record<string, unknown>, idempotencyKey: string) {
  return apiFetch<CreateCharacterResult>('/characters', {
    method: 'POST',
    body,
    idempotencyKey,
    note: 'create character draft',
  });
}

export function getCharacter(characterId: string): Promise<Character> {
  return apiFetch<Character>(`/characters/${encodeURIComponent(characterId)}`, {
    note: 'poll candidates',
  });
}

export function selectCandidate(
  characterId: string,
  candidateIndex: number,
  name: string,
): Promise<{ characterId: string; name: string; status: string }> {
  return apiFetch(`/characters/${encodeURIComponent(characterId)}/select`, {
    method: 'POST',
    body: { candidateIndex, name },
    note: 'lock identity + name',
  });
}

/* ------------------------------------------------------------- commissions */

export type CommissionPreview = {
  previewId?: string;
  premise?: string;
  totalShots?: number;
  cachedShots?: number;
  credits?: number;
  ttlSeconds?: number;
};

/**
 * Free dry run: plan + cache check, no credit hold. Not deployed yet, so a
 * 403/404/501 is an expected answer and must not break the wizard — the
 * summary screen just shows less.
 */
export async function previewCommission(
  characterId: string,
  steer?: string,
): Promise<CommissionPreview | null> {
  try {
    return await apiFetch<CommissionPreview>('/commissions/preview', {
      method: 'POST',
      body: { characterId, steer },
      speculative: true,
      note: 'free commission preview',
    });
  } catch (err) {
    if (err instanceof ApiError) return null;
    throw err;
  }
}

export function createCommission(input: {
  request: string;
  /** Requested runtime. The server clamps it and prices the clamped value, so
      what comes back is what will actually be built and charged for. */
  targetSeconds?: number;
  characterId?: string;
  previewId?: string;
}): Promise<{ commissionId: string; creditsHeld?: number; idempotent?: boolean }> {
  return apiFetch('/commissions', {
    method: 'POST',
    body: { mode: 'episode', ...input },
    // The one call that spends credits, so the one that most needs the guard:
    // without it a double-tapped button buys two episodes.
    idempotencyKey: commissionKey(input),
    note: 'credit hold + pipeline',
  });
}

export function getCommission(commissionId: string): Promise<Commission> {
  return apiFetch<Commission>(`/commissions/${encodeURIComponent(commissionId)}`, {
    note: 'poll commission',
  });
}

/**
 * The films that are still being made.
 *
 * WHY IT EXISTS: the library asked only for /videos, and a film has no video
 * row until the pipeline finishes it. So the making screen told the parent
 * "you can leave this page — it will be waiting in your library", they took it
 * at its word, and the library was empty. Nothing was lost and nothing was
 * broken; there was simply no route that could answer "what have I got coming".
 *
 * NEVER THROWS FOR AN API THAT DOES NOT HAVE THIS ROUTE. GET /commissions
 * lands on its own day, and until it does the deployed Function App has no
 * entry for it in function_app.py's route table and answers 404 — which must
 * cost the parent their finished films as well. Same for a cold-start 500 or a
 * host that answers HTML: an empty in-flight list is a truthful "we cannot
 * currently see any", and the videos call beside it is what reports a session
 * or a network that is genuinely broken.
 *
 * Both wire shapes are accepted for the same reason getStarters() accepts two:
 * a bare array and a {commissions:[...]} envelope are equally likely from a
 * handler that has not shipped yet, and neither is worth a broken shelf.
 */
export async function getInFlightCommissions(): Promise<InFlightCommission[] | null> {
  try {
    const raw = await apiFetch<unknown>('/commissions', { note: 'films still being made' });
    if (Array.isArray(raw)) return raw as InFlightCommission[];
    const rows = (raw as { commissions?: unknown } | null)?.commissions;
    return Array.isArray(rows) ? (rows as InFlightCommission[]) : [];
  } catch (err) {
    if (!(err instanceof ApiError)) throw err;
    // TWO KINDS OF FAILURE, AND THEY WANT OPPOSITE ANSWERS. The library's poll
    // stops when this returns an empty list, so what we hand back decides
    // whether it keeps asking.
    //
    //   404  THE ROUTE IS NOT THERE. An API deployed before GET /commissions
    //        existed answers this on every single call, for ever. Retrying it
    //        every four seconds would be a tight loop against a wall, so it is
    //        reported as "nothing in flight" and the poll goes quiet. The shelf
    //        still shows finished films; it simply cannot show pending ones,
    //        which is exactly the graceful degradation that older API deserves.
    //
    //   else A COLD START, AN EXPIRED TOKEN, A 429. Transient, and the next
    //        tick will very likely succeed. Returning [] here made a blip
    //        indistinguishable from "the film finished": it killed the poll for
    //        good and left the parent on a shelf that would never update again.
    //        null means "no answer", so the caller keeps the rows it has and
    //        asks again.
    //
    // The first draft returned null for BOTH, and Library.test.tsx caught it —
    // the missing-route case went from 1 request to 8 in thirty seconds.
    return err.status === 404 ? [] : null;
  }
}

/* ------------------------------------------------------------------ videos */

/**
 * No `mode` query param, ever.
 *
 * videos.py derives the caller's surface from the ROUTE and its token —
 * an Entra access token on /videos is the parent, an opaque device token on
 * /watch/* carries the childOnly flag off the device row. On AWS the gateway
 * wrote that distinction and a caller could not choose; on one Function App
 * it is function_app.py's route table plus azure/functions/auth.py, which is
 * more reason to keep the client out of it, not less. There was briefly a
 * `?mode=parent` escape hatch here for a handler that read the query string;
 * that was a client-trusted safety gate, and sending it now would assert a
 * trust level the server correctly ignores.
 */
export function getVideos(): Promise<{ videos: Video[] }> {
  return apiFetch<{ videos: Video[] }>('/videos', { note: 'library' });
}

export function approveVideo(videoId: string): Promise<{ videoId: string; status: string }> {
  return apiFetch(`/videos/${encodeURIComponent(videoId)}/approval`, {
    method: 'POST',
    note: 'approve — the safety gate',
  });
}

export function unapproveVideo(videoId: string): Promise<{ videoId: string; status: string }> {
  return apiFetch(`/videos/${encodeURIComponent(videoId)}/approval`, {
    method: 'DELETE',
    note: 'pull from the shelf',
  });
}

export function playbackUrls(videoId: string): Promise<PlaybackResponse> {
  return apiFetch<PlaybackResponse>(`/videos/${encodeURIComponent(videoId)}/playback-urls`, {
    method: 'POST',
    body: {},
    note: 'mint playlist',
  });
}

/* GET /catalog/sample IS DEPLOYED AND THIS CLIENT DELIBERATELY DOES NOT CALL IT.
 *
 * It served components/Sample.tsx — a generic example film, offered before the
 * parent was asked to spend — and that component was never imported by any
 * screen, so the route was reachable from this file and from nowhere else. What
 * actually ships on the screen where money is spent is SeeItFirst in
 * Summary.tsx: a free, token-metered animatic of the parent's OWN character at
 * the length they have chosen, which answers "what am I buying" with their
 * thing rather than a stranger's.
 *
 * The route itself stays: ios/ shows the sample in onboarding and in the parent
 * tab, and the admin config screen reports whether the pool behind it is empty.
 * Only the browser's dead end to it is gone. If a generic sample is ever wanted
 * here — the gallery, before a character exists, is the arguable place — it is
 * one apiFetch, and it must be `anonymous: true`: the handler is an OPEN route
 * (azure/functions/function_app.py), and asking for a token to call it would
 * make an unauthenticated page require a session.
 */

/* --------------------------------------------------------------- previews */

export type PreviewStart = {
  previewId: string;
  status: string;
  /** null for an internal account that bypasses the bucket. */
  previewTokens: number | null;
};

export type PreviewState = {
  commissionId: string;
  status: string;
  animaticStills: string[];
  animaticSecondsPerStill: number;
  failureReason?: string;
};

/**
 * Start a free, token-metered preview.
 *
 * Throws ApiError(402) when the bucket is empty — that is a normal state with
 * a remedy (top up), not a failure, and the caller renders it as such.
 */
export function startPreview(
  characterId: string,
  request: string,
  targetSeconds?: number,
): Promise<PreviewStart> {
  // An OBJECT, not a string: apiFetch stringifies the body itself, and
  // double-encoding sent the server a JSON string where it expected an object,
  // which surfaced as a 500 rather than anything that named the mistake.
  return apiFetch('/commissions/peek', {
    method: 'POST',
    body: { characterId, request, targetSeconds },
    note: 'free preview',
  });
}

export function getPreview(previewId: string): Promise<PreviewState> {
  return apiFetch(`/commissions/${encodeURIComponent(previewId)}`, {
    note: 'preview status',
  });
}

/* ---------------------------------------------------------------- pricing */

export type EpisodeLength = {
  label: string;
  seconds: number;
  segments: number;
  uniqueShots: number;
  credits: number;
};

/**
 * The lengths on offer and what each costs.
 *
 * Fetched rather than hard-coded, deliberately: the server prices a commission
 * from the same clamp the planner uses, and an app carrying its own copy of
 * the formula would quote a number it is not about to be charged.
 */
export async function getPricing(): Promise<EpisodeLength[]> {
  const out = await apiFetch<{ lengths: EpisodeLength[] }>('/pricing', {
    note: 'price list',
  });
  return out.lengths ?? [];
}

/* ------------------------------------------------------------------ warmup */

/**
 * Touch the routes the parent is about to use, so they are warm when they do.
 *
 * WHAT THIS IS ACTUALLY BUYING, measured rather than assumed. The Function App
 * runs on Flex Consumption with `alwaysReady: null`, so a request landing on a
 * fresh instance pays for the Python worker starting AND for the lazy imports
 * that route needs. shared/media_store.py imports azure.storage.blob (209ms)
 * and azure.identity (39ms) INSIDE the call, then authenticates a managed
 * identity over the network — so the first call on a worker to any route that
 * resolves a media URL is far more expensive than the second.
 *
 * That is not a theory. In Jamie's HAR of a library load, three requests went
 * out together:
 *
 *   GET /families/me     72ms     (touches no blobs)
 *   GET /commissions     78ms
 *   GET /videos        1487ms     wait: 1487, everything else 0
 *
 * All server time, no network. /videos is the one that resolves posterUrl.
 *
 * Fire-and-forget, and deliberately not awaited by anything: the sign-in screen
 * must not get slower to make a later screen faster. Errors are swallowed
 * whole — a warmup that surfaced a 500 would turn a silent optimisation into a
 * visible fault.
 *
 * HONEST LIMIT: this warms the instances that serve THESE requests. Flex may
 * route the parent's real call elsewhere, and at this traffic it almost
 * certainly will not — one instance handles many concurrent requests — but
 * "almost certainly" is the correct strength of claim, not "guaranteed". The
 * real fix for a cold start is an always-ready instance, which costs money
 * every month whether anyone signs in or not; this costs two cheap reads at
 * the one moment we know a parent has arrived.
 */
export function prewarm(): void {
  // GETs only, and only ones that are free and side-effect-free. Never
  // /commissions/peek or anything that spends a token.
  void getVideos().catch(() => undefined);
  void getInFlightCommissions().catch(() => undefined);
}
